/**
 * Beta Badge Component
 *
 * A visual indicator that the service is currently in beta testing phase.
 * This component displays a badge to inform users that the service is in active development
 * and may have bugs or limited features.
 *
 * Features:
 * - Eye-catching design with gradient colors
 * - Animated pulse effect to draw attention
 * - Responsive design for mobile and desktop
 * - Optional tooltip with beta information
 */
import { useTranslation } from "react-i18next";

interface BetaBadgeProps {
  variant?: "default" | "compact";
  showPulse?: boolean;
}

/**
 * Beta Badge Component
 *
 * @param variant - Display style: "default" (with text) or "compact" (icon only)
 * @param showPulse - Whether to show animated pulse effect
 */
export default function BetaBadge({
  variant = "default",
  showPulse = true,
}: BetaBadgeProps) {
  const { t } = useTranslation();

  if (variant === "compact") {
    return (
      <span className="relative inline-flex">
        {showPulse && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
        )}
        <span className="relative inline-flex items-center rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow-lg">
          BETA
        </span>
      </span>
    );
  }

  return (
    <div className="relative inline-flex">
      {showPulse && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
      )}
      <div className="relative inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 shadow-lg">
        <svg
          className="h-4 w-4 text-primary-foreground"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
        <span className="text-sm font-semibold text-primary-foreground">
          {t("common.beta", { defaultValue: "베타 서비스" })}
        </span>
      </div>
    </div>
  );
}

/**
 * Beta Banner Component
 *
 * A full-width banner for displaying beta service notice.
 * Can be placed at the top of pages for important announcements.
 */
export function BetaBanner() {
  const { t } = useTranslation();

  return (
    <div className="bg-primary py-2 text-center text-sm font-medium text-primary-foreground">
      <div className="flex items-center justify-center gap-2">
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>
          {t("common.betaNotice", {
            defaultValue:
              "베타 서비스 운영 중입니다. 서비스 이용 중 불편사항이 있으시면 피드백 부탁드립니다.",
          })}
        </span>
      </div>
    </div>
  );
}

